"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _notistack = require("notistack");
var _Box = _interopRequireDefault(require("@mui/material/Box/Box.js"));
var _Typography = _interopRequireDefault(require("@mui/material/Typography/Typography.js"));
var _prosemirrorState = require("prosemirror-state");
var _react2 = require("@tiptap/react");
var _core = require("@tiptap/core");
var _extensionLink = _interopRequireDefault(require("@tiptap/extension-link"));
var _extensionImage = _interopRequireDefault(require("@tiptap/extension-image"));
var _starterKit = _interopRequireDefault(require("@tiptap/starter-kit"));
var _extensionMention = _interopRequireDefault(require("@tiptap/extension-mention"));
var _extensionHighlight = _interopRequireDefault(require("@tiptap/extension-highlight"));
var _extensionUnderline = _interopRequireDefault(require("@tiptap/extension-underline"));
var _extensionTextAlign = _interopRequireDefault(require("@tiptap/extension-text-align"));
var _extensionFileHandler = _interopRequireDefault(require("@tiptap-pro/extension-file-handler"));
var _ToolBar = _interopRequireDefault(require("./ToolBar"));
var _suggestion = require("./suggestion");
var _BubbleMenu = _interopRequireDefault(require("./BubbleMenu"));
var _FloatingMenu = _interopRequireDefault(require("./FloatingMenu"));
var _fileHandler = require("./fileHandler");
var _textEditorStyles = require("./textEditorStyles");
var _textEditorUtils = require("./textEditorUtils");
var _video = require("./custom/video");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const noop = () => void {};
const TextEditorV2 = ({
  editable = true,
  isChat = false,
  initialValue = null,
  minHeight = 200,
  userAccessKey = null,
  fileHandlerConfig = null,
  onChange = noop,
  onBlur = noop,
  onEmpty = noop,
  getSrcUrlFromFile = null,
  getUserList = null,
  getWidgetList = null,
  onHandleSubmit = noop,
  helperText = ''
}) => {
  const {
    getSrcUrlFromToken
  } = fileHandlerConfig || {};
  const {
    initialContent,
    initialAttachments
  } = (0, _textEditorUtils.handleInitialValue)(initialValue, getSrcUrlFromToken, userAccessKey);
  const [isInitialized, setIsInitialized] = (0, _react.useState)(false);
  const [content, _setContent] = (0, _react.useState)(initialContent);
  const [updatedContent, setUpdatedContent] = (0, _react.useState)(initialContent);
  const [attachments, setAttachments] = (0, _react.useState)(initialAttachments);
  const {
    enqueueSnackbar
  } = (0, _notistack.useSnackbar)();
  const onError = (0, _react.useCallback)(message => {
    enqueueSnackbar(message, {
      variant: 'error'
    });
  }, [enqueueSnackbar]);
  const CustomExtension = _core.Extension.create({
    name: 'customExtension',
    addKeyboardShortcuts() {
      return {
        Enter: () => {
          if (isChat && !!onHandleSubmit) {
            const json = this.editor.getHTML();
            const isEmpty = this.editor.isEmpty;
            if (!isEmpty) {
              onHandleSubmit(json);
            }
            onChange('');
            this.editor.commands.clearContent();
            return true;
          }
          return false;
        }
      };
    }
  });
  const UserMention = _extensionMention.default.extend({
    name: 'userNames'
  }).configure({
    HTMLAttributes: {
      class: 'mention'
    },
    suggestion: {
      char: '@',
      pluginKey: new _prosemirrorState.PluginKey('userNames'),
      items: async ({
        query
      }) => {
        let userList = [];
        try {
          userList = await getUserList(query);
        } catch (error) {
          console.error('Error fetching user list:', error);
        }
        userList = Array.isArray(userList) ? userList : [];
        return userList.filter(item => item?.userName?.toLowerCase().startsWith(query.toLowerCase())).slice(0, 5);
      },
      ..._suggestion.mentionListSuggest
    }
  });
  const WidgetTags = _extensionMention.default.extend({
    name: 'widgetTags'
  }).configure({
    HTMLAttributes: {
      class: 'mention'
    },
    suggestion: {
      char: '#',
      pluginKey: new _prosemirrorState.PluginKey('widgetTags'),
      items: async ({
        query
      }) => {
        const widgetList = await getWidgetList(query);
        return widgetList.filter(item => item?.title?.toLowerCase().startsWith(query.toLowerCase()));
      },
      ..._suggestion.widgetTagSuggest
    }
  });
  const extensions = (0, _react.useMemo)(() => {
    return [_starterKit.default.configure({
      heading: isChat ? false : {},
      blockquote: isChat ? false : {}
    }), _extensionUnderline.default, _extensionHighlight.default, _extensionLink.default, CustomExtension, UserMention, WidgetTags, _extensionTextAlign.default.configure({
      types: ['heading', 'paragraph']
    }), _extensionImage.default.configure({
      HTMLAttributes: {
        class: 'editor-image'
      }
    }), _video.Video.configure({
      inline: false,
      HTMLAttributes: {
        controls: true,
        autoplay: false,
        loop: false,
        muted: true,
        class: 'editor-video'
      }
    }), ...(fileHandlerConfig ? [_extensionFileHandler.default.configure({
      onDrop: async (editor, files, pos) => {
        const newAttachments = await (0, _fileHandler.handleFile)(editor, files, pos, fileHandlerConfig, onError);
        if (newAttachments && newAttachments.length > 0) {
          setAttachments(prev => [...prev, ...newAttachments]);
        }
      },
      onPaste: async (editor, files, htmlContent) => {
        const newAttachments = await (0, _fileHandler.handleFile)(editor, files, editor.state.selection.from, fileHandlerConfig, onError);
        if (newAttachments && newAttachments.length > 0) {
          setAttachments(prev => [...prev, ...newAttachments]);
        }
      }
    })] : [])];
  }, [isChat, fileHandlerConfig, onError]);
  const onUpdate = (0, _react.useCallback)(({
    editor
  }) => {
    const json = editor.getJSON();
    const isEmpty = editor.isEmpty;
    if (isEmpty) {
      setUpdatedContent(null);
      return;
    }
    setUpdatedContent(json);
  }, []);
  (0, _react.useEffect)(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }
    if (updatedContent === null) {
      onChange('');
      onEmpty(true);
      return;
    }
    const jsonString = JSON.stringify(updatedContent);
    const jsonStringForAPI = (0, _textEditorUtils.transformJsonForAPI)(jsonString, attachments);
    onChange(jsonStringForAPI);
  }, [updatedContent, attachments, onChange, onEmpty, isInitialized]);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Box.default, {
      sx: (0, _textEditorStyles.getTextEditorStyles)(editable, minHeight, isChat),
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_react2.EditorProvider, {
        extensions: extensions,
        content: content,
        editable: editable,
        slotBefore: editable && !isChat ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_ToolBar.default, {}) : null,
        onUpdate: onUpdate,
        onBlur: onBlur,
        children: !isChat && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_FloatingMenu.default, {}), /*#__PURE__*/(0, _jsxRuntime.jsx)(_BubbleMenu.default, {})]
        })
      })
    }), helperText && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Typography.default, {
      color: "error",
      variant: "caption",
      children: helperText
    })]
  });
};
var _default = exports.default = TextEditorV2;